/**
 * Data layer for `csv-xlsx-attachment-on-scheduled-report`. Thin typed fetch wrappers over /api/v1/csv-xlsx-attachment-on-scheduled-report.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CsvXlsxAttachmentOnScheduledReportRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCsvXlsxAttachmentOnScheduledReportResponse {
	count: number;
	items: CsvXlsxAttachmentOnScheduledReportRow[];
}

export interface CreateCsvXlsxAttachmentOnScheduledReportBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/csv-xlsx-attachment-on-scheduled-report";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCsvXlsxAttachmentOnScheduledReportResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCsvXlsxAttachmentOnScheduledReportResponse>(res);
}

export async function create(body: CreateCsvXlsxAttachmentOnScheduledReportBody): Promise<CsvXlsxAttachmentOnScheduledReportRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CsvXlsxAttachmentOnScheduledReportRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const csvXlsxAttachmentOnScheduledReportQueryKeys = {
	all: ["csv-xlsx-attachment-on-scheduled-report"] as const,
	list: () => [...csvXlsxAttachmentOnScheduledReportQueryKeys.all] as const,
	detail: (id: string) => [...csvXlsxAttachmentOnScheduledReportQueryKeys.all, id] as const,
};
